/* eslint-disable  @next/next/no-img-element, complexity, sonarjs/cognitive-complexity */
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DEAL_SPLAT_VARIANT_TWO } from '../../../brands/default/features';
import { SPLAT } from '../../../config/constants/images';
import { formatCurrency } from '../../../helpers/currency';
import { splatPriceDisplayDiscountAndOriginalPrice } from '../../../helpers/deals';
import { replaceImageServer } from '../../../helpers/image';
import { makeUrlAbsolute } from '../../../helpers/url';
import ThemeContext from '../../../providers/ThemeProvider';

const DealCard = ({ deal }) => {
  const theme = useContext(ThemeContext);
  const isVipUser = useSelector((state) => state?.user?.userprofile?.vipUser);
  const router = useRouter();
  const path = router.asPath || '';
  const [displayDiscountAndOriginalPrice, setDisplayDiscountAndOriginalPrice] =
    useState(false);
  useEffect(() => {
    setDisplayDiscountAndOriginalPrice(
      splatPriceDisplayDiscountAndOriginalPrice({ deal, path }),
    );
  }, [deal, path]);
  if (!deal || !deal.images) return null;
  const image = replaceImageServer({ dealId: deal.id, image: deal.images[0] });
  const urlPath = makeUrlAbsolute(
    deal.urlPath ? deal.urlPath : deal.shareUrl || '#',
  );
  const price = formatCurrency(deal?.currency, deal.price, false, true);

  return (
    <>
      <Link href={urlPath ?? ''}>
        <a aria-label={deal.headline} className="deal-card">
          <img alt={deal.headline} className="deal-card__image" src={image} />
          <div className="deal-card__scrim">
            <h2 className="deal-card__headline">{deal.headline}</h2>
            {deal.discount > 0 && (
              <div className="deal-card__prices">
                <p className="deal-card__deal-price">
                  Save {deal.priceIndicative && 'up to '}
                  {isVipUser && deal?.vipDiscountPercentage
                    ? deal?.vipDiscountPercentage
                    : deal.discountPercentage}
                  %
                </p>
                <p className="deal-card__original-price">
                  {formatCurrency(
                    deal?.currency,
                    deal.originalPrice,
                    false,
                    true,
                  )}
                </p>
              </div>
            )}
          </div>
          {displayDiscountAndOriginalPrice && deal.discountPercentage && (
            <div
              className={classNames('deal-card__splat', {
                'deal-card__splat--livingsocial': DEAL_SPLAT_VARIANT_TWO,
              })}
              style={{
                backgroundImage: `url(${SPLAT})`,
              }}
            >
              <p className="deal-card__splat-text deal-card__price-text">
                {displayDiscountAndOriginalPrice ? 'SAVE' : deal.priceText}
              </p>
              <p className="deal-card__splat-text deal-card__price">
                {displayDiscountAndOriginalPrice
                  ? `${deal.discountPercentage}%`
                  : price.split('.')[0]}
                {!displayDiscountAndOriginalPrice && (
                  <span className="deal-card__splat-pence">
                    {price.split('.')[1] ? `.${price.split('.')[1]}` : ''}
                  </span>
                )}
              </p>
            </div>
          )}
        </a>
      </Link>

      <style jsx>{`
        .deal-card {
          width: 100%;
          background-color: #eeeeee;
          position: relative;
          overflow: hidden;
          padding-bottom: 66.9%;
        }
        .deal-card:hover {
          outline: 2px solid ${theme.colors.navlinkactive};
        }
        .deal-card__image {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          z-index: 1;
          background-color: #eeeeee;
        }
        .deal-card__scrim {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          z-index: 2;
          background-image: linear-gradient(transparent, #333333);
          padding: 40px 85px 10px 10px;
          color: #ffffff;
        }
        .deal-card__headline {
          font-weight: bold;
          text-shadow: 0 0 3px #333333;
          font-size: 14px;
          margin-bottom: ${deal.discount > 0 ? '5px' : '0'};
        }
        .deal-card__splat {
          position: absolute;
          bottom: -70px;
          right: -65px;
          padding: 0px 70px 73px 25px;
          width: 120px;
          height: 120px;
          z-index: 3;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          justify-content: flex-end;
          background-size: contain;
          background-position: 50% 50%;
          background-repeat: no-repeat;
        }
        .deal-card__splat--livingsocial {
          bottom: -20px;
          right: -10px;
          padding: 0px 15px 23px 10px;
          width: 75px;
          height: 75px;
          align-items: center;
        }
        .deal-card__prices {
          display: flex;
          align-items: center;
          font-size: 12px;
        }
        .deal-card__deal-price,
        .deal-card__original-price {
          position: relative;
          margin: 0;
          margin-right: 20px;
        }
        .deal-card__splat-pence {
          font-size: 0.6em;
        }
        .deal-card__original-price:after {
          content: '';
          width: calc(100% + 6px);
          height: 1px;
          background-color: #ffffff;
          position: absolute;
          top: calc(50% - 0.5px);
          left: -3px;
          transform-origin: 50% 50%;
          transform: rotateZ(-10deg);
        }
        .deal-card__splat-text {
          color: #ffffff;
          margin: 0;
          line-height: 1em;
          text-transform: ${theme.text.navigation.transform};
        }
        .deal-card__price-text {
          font-size: 12px;
        }
        .deal-card__price {
          font-size: 16px;
          font-weight: bold;
        }
      `}</style>
    </>
  );
};

DealCard.propTypes = {
  deal: PropTypes.object,
};

export default DealCard;
